import { useAppDispatch, useAppSelector } from "../../../Redux/redux-store";
import { assetService, cacheService, searchService } from "../../../Core/Api/services";
import {
  GetCurrentObj,
  GetObjectResults,
  GetSearchResults,
  SetLoadingStates,
  SetObjectResults,
  SetPagination,
  SetSearchResults,
} from "../../../Redux/Reducers/redux-slice-drive";
import { AddChildrenToTreeObj, TreeContext } from "../../../Redux/Reducers/redux-slice-tree";
import {
  GetAssetIdsFilter,
  GetAssetStatusFilter,
  GetConfidentialityFilter,
  GetCreatedbyFilter,
  GetFilters,
  GetPattern,
  GetProviderIdFilter,
  GetTypeFilter,
  SortingInfos,
} from "../../../Redux/Reducers/redux-slice-filters";
import { SearchRequestNative } from "../../../Core/Api/Types/search-request-native";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import { ObjectClass } from "../../../Core/Api/Types/object-class";
import { GridFilterModel, GridPaginationModel, GridSortModel } from "@mui/x-data-grid";
import { SortingType } from "../../../Core/Api/Types/sorting-type";

export const CHILDREN_PAGE_SIZE = 200;
export const TREEVIEW_PAGE_SIZE = 500;

export const useFolderPagination = () => {
  const dispatch = useAppDispatch();

  const currentObject = useAppSelector(GetCurrentObj);
  const resultObjects = useAppSelector(GetObjectResults);
  const headObjectId = cacheService.GetPlatformDriveHeadId();

  async function loadFolderContents(
    paginationModel: GridPaginationModel,
    replace?: boolean,
    filterModel?: GridFilterModel,
    sortModel?: GridSortModel
  ) {
    dispatch(SetPagination({ isNextPageLoading: true }));
    try {
      const parentId = currentObject?.id || headObjectId;
      if (!parentId) return;

      const sortModelMapped: SortingInfos = {
        type: SortingType.Property,
        name: sortModel ? sortModel?.[0]?.field : "name",
        desc: sortModel ? sortModel?.[0]?.sort !== "asc" : false,
      };

      const quickFilterValues =
        filterModel && filterModel.quickFilterValues ? filterModel.quickFilterValues.join("") : "";

      const filterModelMapped = {
        pattern: quickFilterValues,
        metadata: filterModel
          ? filterModel.items.map((item) => {
              return {
                name: item.field,
                value: item.value,
              };
            })
          : [],
      };

      // In case of external Access parent object -> search directly children by it's ids available in virtualHeadChildrenIds meta value
      let virtualHeadMetaId = cacheService.GetMetadata().find((x) => x.tagName === "virtualHeadChildrenIds")?.id;
      let virtualHeadChildrenIds = currentObject?.metadata?.find((x) => x.id === virtualHeadMetaId);

      try {
        const request = new SearchRequestNative({
          assetIds: virtualHeadChildrenIds ? virtualHeadChildrenIds.value : null,
          hideTitleWithoutAsset: false,
          classifications: [ObjectClass.Drive],
          outputLayer: ObjectLayer.Asset,
          pattern: filterModelMapped.pattern,
          limit: paginationModel.pageSize,
          page: paginationModel.page,
          parentObjectIds: virtualHeadChildrenIds ? null : [parentId],
          sort: sortModelMapped,
          onlyDirectChildren: true,
        });
        const res = await searchService.SearchBasic(request);
        // const res = await assetService.GetChildrenByObjectId(parentId, pageSize, [], page);

        if (res.success && res.results) {
          // If first child is already in resultObject, it means we're done searching
          // This prevents wrong totalChildrenCount from back-end due to permissions to trigger additional searches
          if (!replace && resultObjects.findIndex((obj) => obj.id === res.results![0].id) !== -1) {
            dispatch(
              SetPagination({
                isNextPageLoading: false,
                totalChildrenCount: resultObjects.length,
                hasNextPage: false,
                loadedChildrenCount: resultObjects.length,
              })
            );
            dispatch(SetLoadingStates({ folderLoading: false }));

            return;
          }
          dispatch(SetPagination({ totalChildrenCount: res.count ?? 0 }));

          if (replace) {
            dispatch(SetObjectResults(res.results ?? []));
            dispatch(SetLoadingStates({ folderLoading: false }));
            dispatch(SetPagination({ loadedChildrenCount: (res.results ?? []).length }));
          } else {
            let newResultObject = [...resultObjects, ...(res.results ?? [])];
            dispatch(SetPagination({ loadedChildrenCount: newResultObject.length }));
            dispatch(SetObjectResults(newResultObject));
          }

          dispatch(
            AddChildrenToTreeObj({
              parentId: parentId,
              children: res.results ?? [],
              context: TreeContext.DriveTreeview,
            })
          );
        }
      } catch (error) {
        console.error("Error fetching children:", error);
        dispatch(SetPagination({ isNextPageLoading: false }));
      }
    } catch (error) {
      console.error("Error loading page:", error);
    } finally {
      dispatch(SetPagination({ isNextPageLoading: false }));
    }
  }

  return { loadFolderContents };
};

export const useSearchPagination = () => {
  const dispatch = useAppDispatch();

  const searchResults = useAppSelector(GetSearchResults);

  const pattern = useAppSelector(GetPattern);
  const filters = useAppSelector(GetFilters);
  const typeFilter = useAppSelector(GetTypeFilter);
  const statusFilter = useAppSelector(GetAssetStatusFilter);
  const confidentialityFilter = useAppSelector(GetConfidentialityFilter);
  const createdbyFilter = useAppSelector(GetCreatedbyFilter);
  const providerIdFilter = useAppSelector(GetProviderIdFilter);
  const assetIdsFilter = useAppSelector(GetAssetIdsFilter);

  async function loadSearchContent(
    paginationModel: GridPaginationModel,
    replace?: boolean,
    filterModel?: GridFilterModel,
    sortModel?: GridSortModel
  ) {
    dispatch(SetPagination({ isNextPageLoading: true }));

    const searchRequest = new SearchRequestNative({
      outputLayer: ObjectLayer.Asset,
      hideTitleWithoutAsset: false,
      pattern: pattern,
      metadata: filters,
      classifications: [ObjectClass.Drive],
      statuses: statusFilter,
      onlyConfidential: confidentialityFilter,
      titleCustomerIds: null,
      types: typeFilter,
      assetIds: assetIdsFilter,
      createdBy: createdbyFilter?.ids,
      providerIds: providerIdFilter?.ids,
      limit: paginationModel.pageSize ?? CHILDREN_PAGE_SIZE,
      page: paginationModel.page,
    });

    try {
      const res = await searchService.SearchBasic(searchRequest);

      if (res.success) {
        // If first child is already in searchResults, it means we're done searching
        // This prevents wrong totalChildrenCount from back-end due to permissions to trigger additional searches
        if (!replace && searchResults.findIndex((obj) => obj.id === res.results![0].id) !== -1) {
          dispatch(
            SetPagination({
              isNextPageLoading: false,
              totalChildrenCount: searchResults.length,
              hasNextPage: false,
              loadedChildrenCount: searchResults.length,
            })
          );
          dispatch(SetLoadingStates({ folderLoading: false }));

          return;
        }

        dispatch(SetPagination({ totalChildrenCount: res.count ?? 0 }));

        if (replace) {
          dispatch(SetSearchResults(res.results ?? []));
          dispatch(SetLoadingStates({ folderLoading: false }));
          dispatch(SetPagination({ loadedChildrenCount: (res.results ?? []).length }));
        } else {
          let newResultObject = [...searchResults, ...(res.results ?? [])];
          dispatch(SetPagination({ loadedChildrenCount: newResultObject.length }));
          dispatch(SetSearchResults(newResultObject));
        }
      }
    } catch (error) {
      console.error("Error searching:", error);
      dispatch(SetPagination({ isNextPageLoading: false }));
    } finally {
      dispatch(SetPagination({ isNextPageLoading: false }));
    }
  }

  return { loadSearchContent };
};
